/**
 * Guardrails for AI text. LawBee's AI helps the firm's staff (intake summaries, draft updates);
 * it must never give legal advice in anything a client may read. Client-facing drafts are always
 * approved by a lawyer before sending, and are screened here as a second line of defence.
 */

export const NOT_LEGAL_ADVICE_LABEL = 'AI summary. Not legal advice. Review the intake yourself before relying on it.';

export const CLIENT_DISCLAIMER = 'This message is a status update and is not legal advice.';

/** System prompt rules shared by every AI task. */
export const BASE_RULES = [
  'You assist the staff of a Canadian law firm with administrative writing.',
  'Never give legal advice, legal opinions, predictions of outcomes, assessments of the merits or value of a case, or recommendations about what a person should do legally.',
  'Never state or imply that a lawyer-client relationship exists unless the firm said so.',
  'Never comment on whether a conflict of interest exists.',
  'Be factual and neutral; only use facts present in the input; do not invent dates, amounts or names.',
].join(' ');

const ADVICE_PATTERNS: [RegExp, string][] = [
  [/\byou (should|must|need to|ought to) (sue|file|settle|plead|sign|accept|reject|appeal|claim)\b/i, 'tells the client what to do legally'],
  [/\b(i|we) (advise|recommend|suggest) (that )?you\b/i, 'gives a recommendation'],
  [/\byou (will|would|are likely to|are going to) (win|lose|succeed|get|receive|be awarded)\b/i, 'predicts an outcome'],
  [/\b(strong|weak|good|solid|winnable) (case|claim)\b/i, 'assesses the merits'],
  [/\byour (case|claim) is worth\b/i, 'values the case'],
  [/\b(guarantee|guaranteed)\b/i, 'makes a guarantee'],
  [/\blegally,? you\b/i, 'states a legal position'],
  [/\b(is|is not|isn't) (legal|illegal|lawful|unlawful)\b/i, 'gives a legal opinion'],
  [/\bno conflict of interest\b/i, 'decides a conflict question'],
  [/\b(liable|liability) (is|lies|rests)\b/i, 'assigns liability'],
];

export type GuardrailResult = { ok: boolean; flags: string[] };

/** Screen text that a client may read for legal-advice phrasing. */
export function screenClientText(text: string): GuardrailResult {
  const flags = ADVICE_PATTERNS.filter(([re]) => re.test(text)).map(([, why]) => why);
  return { ok: flags.length === 0, flags: [...new Set(flags)] };
}

/** Deterministic intake summary used when AI is off or not configured (no key). */
export function stubIntakeSummary(input: { name: string; matterType: string; answers: Record<string, unknown>; labels: Record<string, string>; parties: { name: string; role: string }[]; missing: string[] }): string {
  const lines: string[] = [];
  lines.push(`${input.name} submitted a ${input.matterType} intake.`);
  const facts = Object.entries(input.answers)
    .filter(([k, v]) => !['first_name', 'last_name', 'email', 'phone', 'other_parties'].includes(k) && v !== '' && v !== null && v !== undefined)
    .slice(0, 8)
    .map(([k, v]) => `${input.labels[k] ?? k}: ${Array.isArray(v) ? v.map((x) => (typeof x === 'object' && x ? (x as { name?: string }).name : String(x))).join(', ') : String(v)}`);
  if (facts.length) lines.push(`Key facts: ${facts.join('; ')}.`);
  if (input.parties.length) lines.push(`Parties named for the conflict check: ${input.parties.map((p) => `${p.name} (${p.role.replace(/_/g, ' ')})`).join(', ')}.`);
  if (input.missing.length) lines.push(`Still missing: ${input.missing.join(', ')}.`);
  return lines.join(' ');
}

/** Deterministic client update draft used when AI is off or not configured. */
export function stubUpdateDraft(input: { clientFirstName: string; matterTitle: string; stage: string; firmName: string; lawyerName: string; recentNotes: string[] }): { subject: string; body: string } {
  return {
    subject: `Update on ${input.matterTitle}`,
    body: `Hello ${input.clientFirstName},\n\nA quick update from ${input.firmName}: your matter "${input.matterTitle}" is currently at the stage "${input.stage}". ${input.lawyerName} is working on your file and will contact you if anything is needed from you.\n\nIf you have questions, reply to this email.\n\n${input.firmName}\n\n${CLIENT_DISCLAIMER}`,
  };
}
